import React, { useEffect, useRef, useState } from 'react';
import { CompletedEvents, SortOrder } from '@decub8/ui';
import { useRouter } from 'next/router';

import { useGlobalContext } from '@src/hooks/useGlobalContext';
import { EventType } from '@src/ts/constants';
import { ICompletedEvent } from '@src/ts/interfaces';
import { getCompletedEventsData } from '@src/utils/getters';

import { getDataArr } from './utils';

export interface ICompletedPageData {
    total_count: number;
    data: Record<number, ICompletedEvent[]>;
}

// note: these keys must match the id's in the header_values array in the UI library
export enum CompletedEventSortKey {
    FDV = 'FDV',
    ATH_ROI = 'ATH_ROI',
    ATH = 'ATH',
    TOTAL_RAISED = 'TOTAL_RAISED',
    NUM_WHITELISTED = 'NUM_WHITELISTED',
    ENDED_DATE = 'ENDED_DATE',
    TOTAL_DISTRIBUTED = 'TOTAL_DISTRIBUTED',
}

const options = [
    { title: 'IDOs', id: EventType.Crowdfunding },
    { title: 'Learn to Earn', id: EventType.TokenClaim },
];

export const fetchCompletedEventsData = async (
    current_page: number,
    nav: EventType,
    sort_key: CompletedEventSortKey,
    sort_order: SortOrder,
) => {
    const offset = (current_page - 1) * 6;

    const completed_events = await getCompletedEventsData(
        6,
        offset,
        nav,
        sort_key,
        sort_order,
    );

    return completed_events;
};

export const CompletedEventsSection: React.FC<{
    className?: string;
}> = ({ className }) => {
    const [current_page, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [nav, setNav] = useState<EventType>(options[0].id as EventType);
    const [selected_sort_key, setSelectedSortKey] = useState(
        CompletedEventSortKey.ENDED_DATE,
    );
    const [selected_sort_order, setSelectedSortOrder] = useState(
        SortOrder.DESC,
    );

    const [formatted_data, setFormattedData] = useState([]);

    const [all_page_data, setAllPageData] = useState<
        Record<EventType, ICompletedPageData>
    >({
        [EventType.Crowdfunding]: {
            total_count: 0,
            data: {},
        },
        [EventType.TokenClaim]: {
            total_count: 0,
            data: {},
        },
    });

    const completed_events_ref = useRef<HTMLDivElement>(null);
    const { contract_manager } = useGlobalContext();
    const router = useRouter();

    const current_page_data = all_page_data[nav].data[current_page];

    // reset page and sort key when nav changes
    useEffect(() => {
        setCurrentPage(1);
        setSelectedSortKey(CompletedEventSortKey.ENDED_DATE);
        setSelectedSortOrder(SortOrder.DESC);
    }, [nav]);

    // Clear cached data when sort parameters change
    useEffect(() => {
        setAllPageData((prev) => ({
            ...prev,
            [nav]: {
                total_count: 0,
                data: {},
            },
        }));
        setCurrentPage(1);
    }, [selected_sort_key, selected_sort_order, nav]);

    // Fetch data if not present for the current page
    useEffect(() => {
        if (current_page_data) {
            return;
        }

        setLoading(true);

        fetchCompletedEventsData(
            current_page,
            nav,
            selected_sort_key,
            selected_sort_order,
        )
            .then((data) => {
                setAllPageData((prev) => ({
                    ...prev,
                    [nav]: {
                        total_count: prev[nav].total_count || data.total_count,
                        data: {
                            ...prev[nav].data,
                            [current_page]: data.data,
                        },
                    },
                }));
            })
            .catch((err) => {
                console.error('Error fetching data:', err);
            })
            .finally(() => {
                setLoading(false);
            });
    }, [
        current_page,
        nav,
        current_page_data,
        selected_sort_key,
        selected_sort_order,
    ]);

    // update formatted_data whenever the current_page_data or relevant sorting changes
    useEffect(() => {
        if (!current_page_data) {
            setFormattedData([]);
            return;
        }

        const new_formatted_data = getDataArr(current_page_data, router, nav);
        setFormattedData(new_formatted_data || []);
    }, [
        selected_sort_key,
        selected_sort_order,
        current_page_data,
        contract_manager,
        router,
        nav,
    ]);

    const scrollToTop = () => {
        if (completed_events_ref.current) {
            completed_events_ref.current.scrollIntoView({ behavior: 'smooth' });
        }
    };

    return (
        <div className={className}>
            <div ref={completed_events_ref}></div>
            <CompletedEvents
                total_events={
                    !all_page_data[nav].total_count
                        ? 6
                        : all_page_data[nav].total_count
                }
                nav={nav}
                selected_sort_order={selected_sort_order}
                setSelectedSortKey={setSelectedSortKey as (key: string) => void}
                setSelectedSortOrder={setSelectedSortOrder}
                selected_sort_key={selected_sort_key}
                setNav={setNav as (v: string) => void}
                nav_options={options}
                event_list_data={formatted_data}
                current_page={current_page}
                setCurrentPage={setCurrentPage}
                is_loading={loading}
                scrollToTop={scrollToTop}
            />
        </div>
    );
};
